import React, { useRef, useEffect, useState, useMemo, useCallback } from 'react';
import { createEditor, Transforms, Editor, Range } from 'slate';
import { Slate, Editable, withReact, ReactEditor } from 'slate-react';
import { withHistory } from 'slate-history';
import { formatTime } from '../../utils/timeFormatter';
import { blockToSlateValueWithOptions, slateValueToText, slateValueToWords, renderElement, renderLeaf, withWords } from '../../utils/slateWordPlugin';
import { syncWordsWithText } from '../../utils/wordTimestampSync';
import SpeakerSelector from '../SpeakerSelector';
import {
  TrashIcon,
  DocumentDuplicateIcon,
  PlayIcon,
} from '@heroicons/react/24/outline';

/**
 * TranscriptBlock Component
 * 
 * Individual editable transcript segment
 */
const TranscriptBlock = ({
  block,
  speakers,
  isActive,
  isFocused,
  isSelected,
  onTextChange,
  onSpeakerChange,
  onFocus,
  onBlur,
  onSelect,
  onSplit,
  onMergeUp,
  onMergeDown,
  onDelete,
  onDuplicate,
  onSeekToBlock,
  onSeekTo,
  onPlay,
  currentTime,
  isPlaying = false,
  readOnly = false,
  className = '',
}) => {
  const blockRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);
  const [currentWordIndex, setCurrentWordIndex] = useState(-1);
  const mergeCursorSetRef = useRef(false);
  const pendingSaveRef = useRef(null);
  const lastEmittedTextRef = useRef(block.text || '');
  const scrollRestoreTimeoutRef = useRef(null);

  // Check if block has word-level data.
  // Word-level spans only for active block (playing) to reduce DOM/memory; others use plain text.
  const hasWords = block.words && Array.isArray(block.words) && block.words.length > 0;
  const enableWordLevel = hasWords && isActive;
  const slateSourceBlock = useMemo(() => {
    return {
      id: block.id,
      text: typeof block.text === 'string' ? block.text : '',
      words: Array.isArray(block.words) ? block.words : [],
    };
  }, [block.id, block.text, block.words]);

  // Create Slate editor instance with withWords plugin
  const editor = useMemo(() => {
    const e = withWords(withHistory(withReact(createEditor())));
    return e;
  }, []);

  // Convert block data to Slate value
  const initialValue = useMemo(() => {
    return blockToSlateValueWithOptions(slateSourceBlock, { wordLevel: enableWordLevel });
  }, [slateSourceBlock, enableWordLevel]);

  const [slateValue, setSlateValue] = useState(initialValue);
  const lastSlateTextRef = useRef(typeof block.text === 'string' ? block.text : '');
  
  const slateValueHasWordNodes = useCallback((value) => {
    if (!Array.isArray(value)) return false;
    for (const node of value) {
      const children = node?.children;
      if (!Array.isArray(children)) continue;
      for (const child of children) {
        if (child?.type === 'word') return true;
      }
    }
    return false;
  }, []);

  /**
   * Rehydrate word-level nodes when a block becomes active.
   * Uses current editor text (slateValueToText) to preserve in-flight typing.
   */
  useEffect(() => {
    if (!enableWordLevel || !hasWords) return;
    if (slateValueHasWordNodes(slateValue)) return;

    const currentText = slateValueToText(slateValue);
    const syncedWords = syncWordsWithText(currentText, block.words || [], block.start, block.end);
    const blockForWords = { ...slateSourceBlock, text: currentText, words: syncedWords };
    const newValue = blockToSlateValueWithOptions(blockForWords, { wordLevel: true });
    lastSlateTextRef.current = slateValueToText(newValue);
    setSlateValue(newValue);

    try {
      Transforms.delete(editor, {
        at: {
          anchor: Editor.start(editor, []),
          focus: Editor.end(editor, []),
        },
      });
      Transforms.insertNodes(editor, newValue);
    } catch (error) {
      console.warn('Failed to rehydrate word-level Slate nodes:', error);
    }
  }, [
    enableWordLevel,
    hasWords,
    slateValue,
    slateValueHasWordNodes,
    slateSourceBlock,
    block.words,
    block.start,
    block.end,
    editor,
  ]);

  /**
   * Collapse word-level nodes to plain text when block becomes inactive.
   * Frees word spans immediately to reduce detached DOM.
   */
  useEffect(() => {
    if (enableWordLevel) return;
    if (!slateValueHasWordNodes(slateValue)) return;

    const currentText = slateValueToText(slateValue);
    const blockForPlain = { ...slateSourceBlock, text: currentText };
    const newValue = blockToSlateValueWithOptions(blockForPlain, { wordLevel: false });
    lastSlateTextRef.current = currentText;
    setSlateValue(newValue);

    try {
      Transforms.delete(editor, {
        at: {
          anchor: Editor.start(editor, []),
          focus: Editor.end(editor, []),
        },
      });
      Transforms.insertNodes(editor, newValue);
    } catch (error) {
      console.warn('Failed to collapse word-level Slate nodes:', error);
    }
  }, [
    enableWordLevel,
    slateValue,
    slateValueHasWordNodes,
    slateSourceBlock,
    editor,
  ]);

  /**
   * Find which word is currently playing based on time
   */
  useEffect(() => {
    if (!hasWords || !isActive) {
      setCurrentWordIndex(-1);
      return;
    }
    
    const wordIdx = block.words.findIndex(
      word => currentTime >= word.start && currentTime < word.end
    );
    
    if (wordIdx !== currentWordIndex) {
      setCurrentWordIndex(wordIdx);
    }
  }, [currentTime, hasWords, isActive, block.words, block.id, currentWordIndex, isPlaying]);


  /**
   * Handle Slate value change
   */
  const handleSlateChange = useCallback((value) => {
    setSlateValue(value);
    
    // Convert Slate value to plain text
    const newText = slateValueToText(value);
    lastSlateTextRef.current = newText;
    
    // Only trigger onChange if text actually changed
    if (newText !== block.text) {
      // Debounce global updates to avoid pushing huge block arrays on every keystroke
      if (pendingSaveRef.current) {
        clearTimeout(pendingSaveRef.current);
      }

      pendingSaveRef.current = setTimeout(() => {
        // Extract updated words array from Slate value
        const updatedWords = slateValueToWords(value, block.words);
        
        // Sync words with text (preserve timestamps where possible)
        const syncedWords = syncWordsWithText(newText, updatedWords, block.start, block.end);

        if (newText !== lastEmittedTextRef.current) {
          lastEmittedTextRef.current = newText;
          onTextChange(block.id, newText, syncedWords);
        }
      }, 350);
    }
  }, [block.id, block.text, block.words, block.start, block.end, onTextChange]);

  // Flush any pending debounced update and timeouts on blur/unmount
  useEffect(() => {
    return () => {
      if (pendingSaveRef.current) {
        clearTimeout(pendingSaveRef.current);
        pendingSaveRef.current = null;
      }
      if (scrollRestoreTimeoutRef.current) {
        clearTimeout(scrollRestoreTimeoutRef.current);
        scrollRestoreTimeoutRef.current = null;
      }
    };
  }, []);

  /**
   * Handle key down events for Slate
   */
  const handleKeyDown = useCallback((e) => {
    // Shift+Backspace at start: Merge with previous block
    if (e.shiftKey && e.key === 'Backspace') {
      try {
        const { selection } = editor;
        if (selection && Range.isRange(selection)) {
          // Check if cursor is at the start of the block
          const isAtStart = Editor.isStart(editor, selection.anchor, []);
          
          // Also check if cursor is at position 0 by checking text before cursor
          const range = Editor.range(editor, Editor.start(editor, []), selection.anchor);
          const textBeforeCursor = Editor.string(editor, range);
          const isAtStartOfText = textBeforeCursor.length === 0;
          
          if (isAtStart || isAtStartOfText) {
            e.preventDefault(); // Prevent default behavior only when merging
            e.stopPropagation(); // Stop event propagation
            onMergeUp(block.id);
            return;
          }
        }
      } catch (error) {
        console.error('Merge up error:', error);
      }
      // If not at start, allow normal backspace behavior (don't prevent default)
    }

    // Shift+Delete at end: Merge with next block
    if (e.shiftKey && e.key === 'Delete') {
      e.preventDefault(); // Prevent default behavior immediately
      e.stopPropagation(); // Stop event propagation
      
      try {
        const { selection } = editor;
        if (selection && Range.isRange(selection)) {
          // Check if cursor is at the end of the block
          const isAtEnd = Editor.isEnd(editor, selection.anchor, []);
          
          // Also check if cursor is at the end of text
          const currentText = slateValueToText(slateValue);
          const range = Editor.range(editor, Editor.start(editor, []), selection.anchor);
          const textBeforeCursor = Editor.string(editor, range);
          const isAtEndOfText = textBeforeCursor.length === currentText.length;
          
          if (isAtEnd || isAtEndOfText) {
            onMergeDown(block.id);
            return;
          }
        }
      } catch (error) {
        console.error('Merge down error:', error);
      }
      return; // Always return to prevent default behavior
    }

    // Prevent Enter from creating new paragraphs
    if (e.key === 'Enter' && !e.ctrlKey && !e.metaKey) {
      e.preventDefault();
      
      // Shift+Enter: Split block at cursor position
      if (e.shiftKey) {
        try {
          // Get current selection position
          const { selection } = editor;
          if (selection && Range.isRange(selection)) {
            const currentText = slateValueToText(slateValue);
            
            // Use Slate's API to get text before cursor
            const range = Editor.range(editor, Editor.start(editor, []), selection.anchor);
            const textBeforeCursor = Editor.string(editor, range);
            const cursorPosition = textBeforeCursor.length;
            
            onSplit(block.id, cursorPosition, currentText);
          }
        } catch (error) {
          console.error('Split error:', error);
        }
      }
      return;
    }

    // Split block: Cmd+Shift+Space OR Cmd+Shift+Enter (alternative shortcut)
    const isSplitShortcut = 
      ((e.metaKey || e.ctrlKey) && e.shiftKey && e.code === 'Space') ||
      ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === 'Enter');

    if (isSplitShortcut) {
      e.preventDefault();
      try {
        const { selection } = editor;
        if (selection && Range.isRange(selection)) {
          const currentText = slateValueToText(slateValue);
          
          // Use Slate's API to get text before cursor
          const range = Editor.range(editor, Editor.start(editor, []), selection.anchor);
          const textBeforeCursor = Editor.string(editor, range);
          const cursorPosition = textBeforeCursor.length;
          
          onSplit(block.id, cursorPosition, currentText);
        }
      } catch (error) {
        console.error('Split error:', error);
      }
    }
  }, [editor, slateValue, block.id, onSplit, onMergeUp, onMergeDown]);

  /**
   * Handle focus
   */
  const handleFocus = () => {
    onFocus(block.id);
  };

  /**
   * Handle blur
   */
  const handleBlur = () => {
    if (pendingSaveRef.current) {
      clearTimeout(pendingSaveRef.current);
      pendingSaveRef.current = null;

      const newText = slateValueToText(slateValue);
      if (newText !== lastEmittedTextRef.current) {
        const updatedWords = slateValueToWords(slateValue, block.words);
        const syncedWords = syncWordsWithText(newText, updatedWords, block.start, block.end);
        lastEmittedTextRef.current = newText;
        onTextChange(block.id, newText, syncedWords);
      }
    }
    onBlur();
  };

  /**
   * Handle word click - seek to specific word
   */
  const handleWordClick = useCallback(async (wordData) => {
    if (!wordData || wordData.start === undefined) return;
    
    try {
      await onSeekTo(wordData.start);
      if (onPlay) {
        onPlay();
      }
    } catch (error) {
      console.error('[TranscriptBlock] Word click seek failed:', error);
    }
  }, [onSeekTo, onPlay]);

  /**
   * Handle block click - for video playback and selection
   */
  const handleBlockClick = async (e) => {
    // Prevent any scroll behavior - preventDefault stops browser default scroll
    e.preventDefault();
    e.stopPropagation();
    
    // Also prevent any potential scroll from focus events
    if (blockRef.current) {
      // Temporarily disable scrollIntoView completely
      const originalScrollIntoView = blockRef.current.scrollIntoView;
      blockRef.current.scrollIntoView = () => {};

      if (scrollRestoreTimeoutRef.current) {
        clearTimeout(scrollRestoreTimeoutRef.current);
      }
      scrollRestoreTimeoutRef.current = setTimeout(() => {
        scrollRestoreTimeoutRef.current = null;
        if (blockRef.current && document.contains(blockRef.current)) {
          blockRef.current.scrollIntoView = originalScrollIntoView;
        }
      }, 100);
    }
    
    // Check if clicking on a word span - if so, let word click handler deal with it
    const isWordSpan = e.target.closest('[data-from]');
    if (isWordSpan) {
      return; // Let word click handler deal with it
    }
    
    // If clicking inside editor while editing, don't seek
    const isInsideEditor = e.target.closest('[role="textbox"]');
    if (isInsideEditor) {
      return;
    }
    
    // Multi-select with Ctrl/Cmd
    if (e.ctrlKey || e.metaKey) {
      onSelect(block.id, true);
      return;
    }
    
    // Regular click - seek to this block's start time (only if not playing)
    if (!isPlaying) {
      try {
        await onSeekToBlock(block.id);
      } catch (error) {
        console.error('[TranscriptBlock] Seek failed on block click:', error);
      }
    }
  };

  /**
   * Handle play from block start
   */
  const handlePlayFromStart = async () => {
    try {
      await onSeekToBlock(block.id);
      // Start playing video after seeking completes
      if (onPlay) {
        onPlay();
      }
    } catch (error) {
      console.error('[TranscriptBlock] Seek failed on play from start:', error);
    }
  };

  /**
   * Sync Slate value with block data when block changes externally (split, merge, undo).
   * Only sync when parent's block.text differs from what we last emitted; avoids overwriting
   * local typing that has not yet been emitted (debounced 350ms).
   */
  useEffect(() => {
    if (block.text !== lastEmittedTextRef.current) {
      const newValue = blockToSlateValueWithOptions(slateSourceBlock, { wordLevel: enableWordLevel });
      lastSlateTextRef.current = slateValueToText(newValue);
      lastEmittedTextRef.current = block.text;
      setSlateValue(newValue);

      try {
        Transforms.delete(editor, {
          at: {
            anchor: Editor.start(editor, []),
            focus: Editor.end(editor, []),
          },
        });
        Transforms.insertNodes(editor, newValue);
      } catch (error) {
        console.warn('Failed to update Slate editor:', error);
      }
    }
  }, [block.text, slateSourceBlock, enableWordLevel, editor]);

  /**
   * Reset merge cursor flag when block ID changes
   */
  useEffect(() => {
    mergeCursorSetRef.current = false;
  }, [block.id]);

  /**
   * Slate editor cleanup on unmount - deselect/blur to help release DOM references
   * Reduces detached DOM when Virtuoso unmounts blocks
   */
  useEffect(() => {
    return () => {
      try {
        ReactEditor.deselect(editor);
        ReactEditor.blur(editor);
      } catch (_) {
        // Editor may already be torn down
      }
    };
  }, [editor]);

  /**
   * Focus Slate editor when block is focused
   * Don't move cursor - let Slate handle cursor position from user click
   * Cleanup cancels pending raf/timeouts to avoid memory leaks on unmount
   */
  useEffect(() => {
    if (!isFocused || !editor) return;

    let rafId;
    const timeouts = [];

    try {
      const scrollContainer = blockRef.current?.closest('[id="transcript-blocks-container"]') ||
                             blockRef.current?.closest('.overflow-y-auto');
      let savedScrollTop = 0;
      let savedScrollLeft = 0;

      if (scrollContainer) {
        savedScrollTop = scrollContainer.scrollTop;
        savedScrollLeft = scrollContainer.scrollLeft;
      }

      rafId = requestAnimationFrame(() => {
        try {
          const editorElement = ReactEditor.toDOMNode(editor, editor);
          if (editorElement && document.contains(editorElement)) {
            editorElement.focus({ preventScroll: true });

            if (scrollContainer) {
              const restoreScroll = () => {
                if (scrollContainer.scrollTop !== savedScrollTop ||
                    scrollContainer.scrollLeft !== savedScrollLeft) {
                  scrollContainer.scrollTop = savedScrollTop;
                  scrollContainer.scrollLeft = savedScrollLeft;
                }
              };

              restoreScroll();
              timeouts.push(setTimeout(restoreScroll, 0));
              timeouts.push(setTimeout(restoreScroll, 10));
              timeouts.push(setTimeout(restoreScroll, 50));
              timeouts.push(setTimeout(restoreScroll, 100));
            }
          }
        } catch (err) {
          console.warn('Failed to focus Slate editor:', err);
        }
      });
    } catch (error) {
      console.warn('Failed to focus Slate editor:', error);
    }

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      timeouts.forEach(clearTimeout);
    };
  }, [isFocused, editor]);

  /**
   * Get block state classes
   */
  // Keep border width constant (always 1px) to avoid layout shift on focus/active.
  // Active state is shown by the absolute green bar (left), not by border-l-4.
  const getBlockClasses = () => {
    const classes = [
      'group',
      'relative',
      'p-4',
      'rounded-lg',
      'border',
      'border-[1px]', // Explicit 1px so focus/active never change box size
      'transition-colors',
      'duration-150',
      'hover:shadow-md',
    ];

    if (isActive) {
      classes.push('border-green-500', 'bg-green-50');
    } else if (isSelected) {
      classes.push('border-blue-500', 'bg-blue-50');
    } else if (isFocused) {
      classes.push('border-blue-400', 'bg-blue-50/50');
    } else {
      classes.push('border-gray-200', 'hover:border-gray-300');
    }

    return classes.join(' ');
  };

  /**
   * Prevent scroll when block becomes active or focused
   * Cleanup cancels pending raf/timeouts to avoid memory leaks on unmount
   */
  useEffect(() => {
    if (!(isActive || isFocused) || !blockRef.current) return;

    const element = blockRef.current;
    if (!document.contains(element)) return;

    const originalScrollIntoView = element.scrollIntoView;
    element.scrollIntoView = () => {};

    const scrollContainer = element.closest('[id="transcript-blocks-container"]') ||
                           element.closest('.overflow-y-auto');
    const timeouts = [];
    let rafId;

    if (scrollContainer) {
      const savedScrollTop = scrollContainer.scrollTop;
      const savedScrollLeft = scrollContainer.scrollLeft;

      const checkScroll = () => {
        if (document.contains(scrollContainer) &&
            (scrollContainer.scrollTop !== savedScrollTop ||
             scrollContainer.scrollLeft !== savedScrollLeft)) {
          scrollContainer.scrollTop = savedScrollTop;
          scrollContainer.scrollLeft = savedScrollLeft;
        }
      };

      rafId = requestAnimationFrame(checkScroll);
      timeouts.push(setTimeout(checkScroll, 0));
      timeouts.push(setTimeout(checkScroll, 10));
      timeouts.push(setTimeout(checkScroll, 50));
    }

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      timeouts.forEach(clearTimeout);
      if (document.contains(element)) {
        element.scrollIntoView = originalScrollIntoView;
      }
    };
  }, [isActive, isFocused]);

  return (
    <div
      ref={blockRef}
      id={`block-${block.id}`}
      tabIndex={-1} // Prevent focus from causing scroll
      className={`${getBlockClasses()} ${className} ${readOnly ? 'cursor-default opacity-90' : 'cursor-pointer'}`}
      onClick={handleBlockClick}
      onMouseEnter={() => !readOnly && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{ 
        scrollMargin: 0,
        scrollPadding: 0,
        scrollSnapAlign: 'none',
      }}
    >
      {/* Header: Speaker & Timestamp */}
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="flex items-center gap-3">
          <SpeakerSelector
            value={block.speaker}
            speakers={speakers}
            onChange={(newSpeaker) => onSpeakerChange(block.id, newSpeaker)}
            disabled={readOnly}
          />

          <button
            onClick={(e) => {
              e.stopPropagation(); // Prevent block click
              handlePlayFromStart();
            }}
            className="p-1 hover:bg-gray-100 rounded transition-colors"
            title="Play from here"
          >
            <PlayIcon className="h-4 w-4 text-gray-600" />
          </button>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500 font-mono">
            {formatTime(block.start)} → {formatTime(block.end)}
          </span>

          {/* Action Buttons (visible on hover) - hide in read-only mode */}
          {!readOnly && (
            <div
              className={`flex items-center gap-1 transition-opacity ${
                isHovered ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <button
                onClick={() => onDuplicate(block.id)}
                className="p-1 hover:bg-gray-100 rounded transition-colors"
                title="Duplicate (Cmd+D)"
              >
                <DocumentDuplicateIcon className="h-4 w-4 text-gray-600" />
              </button>

              <button
                onClick={() => onDelete(block.id)}
                className="p-1 hover:bg-red-100 rounded transition-colors"
                title="Delete (Cmd+Backspace)"
              >
                <TrashIcon className="h-4 w-4 text-red-600" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Slate Editor - always editable with word highlighting */}
      <div 
        id={`block-content-${block.id}`}
        className={`text-base text-gray-900 leading-relaxed min-h-[2rem] p-3 rounded transition-all duration-200 ${
          isActive && !hasWords ? 'bg-yellow-50' : ''
        } ${readOnly ? 'cursor-not-allowed opacity-70' : ''}`}
        style={{
          letterSpacing: 'normal',
          wordSpacing: 'normal',
        }}
      >
        <Slate
          editor={editor}
          initialValue={initialValue}
          value={slateValue}
          onChange={handleSlateChange}
        >
          <Editable
            readOnly={readOnly}
            onKeyDown={handleKeyDown}
            onFocus={handleFocus}
            onBlur={handleBlur}
            renderElement={(props) => renderElement(props, {
              isPlaying,
              currentTime,
              onWordClick: enableWordLevel ? handleWordClick : null,
            })}
            renderLeaf={renderLeaf}
            placeholder="Type your transcript here..."
            spellCheck={!readOnly}
            className="outline-none focus:ring-0"
            style={{
              whiteSpace: 'pre-wrap',
              wordWrap: 'break-word',
              letterSpacing: 'normal',
              wordSpacing: 'normal',
            }}
          />
        </Slate>
      </div>

      {/* Metadata Indicator */}
      {block.metadata?.isEdited && (
        <div className="absolute top-2 right-2">
          <div
            className="w-2 h-2 bg-orange-400 rounded-full"
            title="Manually edited"
          />
        </div>
      )}

      {/* Active Indicator */}
      {isActive && (
        <div className="absolute left-0 top-0 bottom-0 w-1 bg-green-500 rounded-l-lg" />
      )}
      
      {/* Read-only Indicator */}
      {readOnly && (
        <div className="absolute top-2 left-2">
          <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        </div>
      )}
    </div>
  );
};

// Memoize component to prevent unnecessary re-renders
export default React.memo(TranscriptBlock, (prevProps, nextProps) => {
  // Custom comparison - use reference equality for speakers (stable from useMemo)
  return (
    prevProps.block.id === nextProps.block.id &&
    prevProps.block.text === nextProps.block.text &&
    prevProps.block.speaker === nextProps.block.speaker &&
    prevProps.isActive === nextProps.isActive &&
    prevProps.isFocused === nextProps.isFocused &&
    prevProps.isSelected === nextProps.isSelected &&
    prevProps.currentTime === nextProps.currentTime &&
    prevProps.isPlaying === nextProps.isPlaying &&
    prevProps.readOnly === nextProps.readOnly &&
    prevProps.speakers === nextProps.speakers
  );
});


